const SLUG_CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/** Random name for an uploaded file, suffixed with the owning field so keys stay traceable. */
export function generateRandomSlug(fieldId: string): string {
	let result = '';
	for (let i = 0; i < 12; i++) {
		result += SLUG_CHARS.charAt(Math.floor(Math.random() * SLUG_CHARS.length));
	}
	return result + fieldId.slice(-4);
}

type UploadHeaders = Record<string, string>;

type UploadResponse = {
	xhr: XMLHttpRequest;
	response: (onProgress?: (progress: number) => void) => Promise<string | false>;
};

export const uploadFileWithProgress = (
	url: string,
	body: FormData | Blob | ArrayBuffer,
	headers: UploadHeaders | '' = {
		'content-type': 'application/octet-stream',
	},
	reqType: 'PUT' | 'POST' = 'PUT',
	removeHeaders = false
): UploadResponse => {
	let effectiveHeaders: UploadHeaders = typeof headers === 'string' ? {} : headers;
	if (removeHeaders) effectiveHeaders = {};

	const xhr = new XMLHttpRequest();

	const response = (onProgress?: (progress: number) => void): Promise<string | false> =>
		new Promise((resolve, reject) => {
			xhr.upload.addEventListener('progress', (event) => {
				if (event.lengthComputable && onProgress) {
					onProgress((event.loaded / event.total) * 100);
				}
			});

			xhr.onerror = (e) => reject(e);
			xhr.onabort = (e) => reject(e);
			xhr.addEventListener('loadend', () => {
				resolve(xhr.readyState === 4 ? xhr.responseText : false);
			});

			xhr.open(reqType, url, true);
			for (const key in effectiveHeaders) {
				xhr.setRequestHeader(key, effectiveHeaders[key]);
			}
			xhr.send(body);
		});

	return { xhr, response };
};

/** The canonical S3 PUT — no headers, because the presigned URL already signs the content type. */
export const uploadToPresignedUrl = async (
	file: Blob,
	presignedUrl: string,
	onProgress?: (percent: number) => void
): Promise<void> => {
	const { xhr, response } = uploadFileWithProgress(presignedUrl, file, '', 'PUT');

	// Capped at 99% — the last percent belongs to whatever the caller does with the result.
	await response((progress) => onProgress?.(Math.min(progress, 99)));

	if (xhr.status < 200 || xhr.status >= 300) {
		throw new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`);
	}
};
